// Library Methods
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import SharedTabs from "src/components/shared/navigation/SharedTabs/SharedTabs";
import TutorialVideos from "./TutorialVideos/TutorialVideos";
import OperatingManual from "./OperatingManual/OperatingManual";
import DataHubGuide from "./DataHubGuide/DataHubGuide";

// Styles
import { PageContainerWrapper } from "src/styles/mui/muiStylesHelper";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

const KnowledgeHub = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { clinicInfo, isLoading } = useClinicContext();

  const canUseEMR = useMemo(
    () => Boolean(clinicInfo?.licensing?.canUseEMR && !isLoading),
    [clinicInfo?.licensing?.canUseEMR, isLoading]
  );

  const [selectedTabId, setSelectedTabId] = useState(() => {
    if (location.pathname.includes("operating-manual")) {
      return 1;
    }
    if (location.pathname.includes("data-hub-guide")) {
      return 2;
    }
    return 0;
  });

  const tabs = [
    {
      id: 0,
      route: "tutorial-videos",
      label: t("Tutorial Videos"),
      showTab: true,
    },
    {
      id: 1,
      route: "operating-manual",
      label: t("Operating Manual"),
      showTab: true,
    },
    {
      id: 2,
      route: "data-hub-guide",
      label: t("Data Hub Guide"),
      showTab: canUseEMR,
    },
  ];

  useEffect(() => {
    // if there is no subpath on the /knowledge-hub url, add the default subpath
    // go to the tutorial videos page
    if (
      !location.pathname.includes("tutorial-videos") &&
      !location.pathname.includes("operating-manual") &&
      !location.pathname.includes("data-hub-guide")
    ) {
      navigate("/knowledge-hub/tutorial-videos");
      setSelectedTabId(0);
    }
  }, [navigate, location.pathname]);

  useEffect(() => {
    // if on the operating-manual page and the user clicks knowledge hub from the navbar dropdown,
    // go to the tutorial videos page
    if (location.pathname.includes("tutorial-videos")) {
      setSelectedTabId(0);
    }
    // if on the tutorial-videos page and the user clicks the help button on the navbar,
    // go to the operating manual page
    if (location.pathname.includes("operating-manual")) {
      setSelectedTabId(1);
    }

    if (location.pathname.includes("data-hub-guide") && !isLoading) {
      if (canUseEMR) {
        setSelectedTabId(2);
      } else {
        navigate("/knowledge-hub/tutorial-videos");
        setSelectedTabId(0);
      }
    }
  }, [navigate, location.pathname, canUseEMR, isLoading]);

  return (
    <Box sx={{ ...PageContainerWrapper(theme), overflow: "clip", gap: "20px" }}>
      <Typography fontSize="16px" fontWeight={600}>
        {t("Knowledge Hub")}
      </Typography>
      <SharedTabs
        tabs={tabs}
        selectedTabId={selectedTabId}
        onChange={(newTabId) => {
          const newRoute = tabs.find((tab) => tab.id === newTabId)?.route;
          navigate(`/knowledge-hub/${newRoute}`);
        }}
      />

      {selectedTabId === 0 && <TutorialVideos />}
      {selectedTabId === 1 && <OperatingManual />}
      {selectedTabId === 2 && canUseEMR && <DataHubGuide />}
    </Box>
  );
};

export default KnowledgeHub;
